import {request} from '..';

export const getWorkshops = async (isRecipes?: boolean) => {
  return request<ToolboxObject.Workshop[]>({
    url: isRecipes ? '/recipes' : '/workshops',
    method: 'GET',
  });
};

export const getRecipes = async () => {
  return getWorkshops(true);
};

export const getWorkshop = async (id: number, isRecipe?: boolean) => {
  return request({
    url: `/${isRecipe ? 'recipes' : 'workshops'}/${id}`,
    method: 'GET',
  });
};

export const getRecipe = async (id: number) => {
  return getWorkshop(id, true);
};

export const viewWorkshop = async (workshopId: number) => {
  return request({
    url: `/workshops/${workshopId}/view`,
    method: 'PUT',
  });
};

export const getWorkshopUsers = async (workshopId: number) => {
  return request({
    url: `/workshops/${workshopId}/users`,
    method: 'GET',
  });
};

export const addWorkshopUsers = async (workshopId: number, data: {userIds: number[]; access: ToolboxObject.WorkshopUserAccess}): Promise<void> => {
  return request({
    url: `/workshops/${workshopId}/users`,
    method: 'POST',
    data,
  });
};

export const updateWorkshopUser = async (workshopId: number, data: {access: ToolboxObject.WorkshopUserAccess; userId: number}): Promise<void> => {
  return request({
    url: `/workshops/${workshopId}/users/${data.userId}`,
    method: 'PUT',
    data,
  });
};

export const deleteWorkshopUser = async (workshopId: number, userId: number): Promise<void> => {
  return request({
    url: `/workshops/${workshopId}/users/${userId}`,
    method: 'DELETE',
  });
};

export const getWorkshopAvailableUsers = async (workshopId: number): Promise<ToolboxObject.User[]> => {
  return request({
    url: `/workshops/${workshopId}/available-users`,
    method: 'GET',
  });
};

export const toggleLikeRecipe = async (recipeId: number, isLiked: boolean) => {
  return request({
    url: `/recipes/${recipeId}/${isLiked ? 'unfavorite' : 'favorite'}`,
    method: 'PUT',
  });
};

export const viewRecipe = async (recipeId: number) => {
  return request({
    url: `/recipes/${recipeId}/view`,
    method: 'PUT',
  });
};

export const saveWorkshop = async (data: {
  title: string;
  sessions: ToolboxObject.WorkshopSession[];
  id: number;
  description: string;
  requirements: ToolboxObject.Info[];
  benefits: ToolboxObject.Info[];
  category?: ToolboxObject.WorkshopCategory | null;
  teaser?: string | null;
  checklist?: ToolboxObject.ChecklistTask[];
  isRecipe?: boolean;
  language: ToolboxObject.Locale;
}) => {
  const {id, isRecipe, category, ...rest} = data;
  return request({
    url: `/${isRecipe ? 'recipes' : 'workshops'}/${id}`,
    method: 'PUT',
    data: {...rest, categoryId: category?.id ?? null},
  });
};

export const saveRecipe = async (data: {
  title: string;
  sessions: ToolboxObject.WorkshopSession[];
  id: number;
  description: string;
  requirements: ToolboxObject.Info[];
  benefits: ToolboxObject.Info[];
  category?: ToolboxObject.WorkshopCategory | null;
  teaser?: string | null;
  checklist?: ToolboxObject.ChecklistTask[];
  language: ToolboxObject.Locale;
}) => {
  return saveWorkshop({...data, isRecipe: true});
};

export const miroBoardFromWorkshop = async (id: number) => {
  return request({
    url: `/workshops/${id}/miro`,
    method: 'POST',
  });
};

export const linkMiroBoardToWorkshop = async (id: number, href: string | null) => {
  return request({
    url: `/workshops/${id}/miro`,
    method: 'PUT',
    data: {href},
  });
};

export const disconnectMiro = () => {
  return request({
    url: '/miro',
    method: 'DELETE',
  });
};

export const createWorkshop = async (data: {
  title: string;
  sessions: ToolboxObject.WorkshopSession[];
  description: string;
  requirements: ToolboxObject.Info[];
  benefits: ToolboxObject.Info[];
  category: ToolboxObject.WorkshopCategory | null;
  teaser: string | null;
  checklist: ToolboxObject.ChecklistTask[];
  isRecipe?: boolean;
  language: ToolboxObject.Locale;
}) => {
  const {isRecipe, category, ...rest} = data;
  return request({
    url: isRecipe ? '/recipes' : `/workshops`,
    method: 'POST',
    data: {...rest, categoryId: category?.id ?? null},
  });
};

export const createRecipe = async (data: {
  title: string;
  sessions: ToolboxObject.WorkshopSession[];
  description: string;
  requirements: ToolboxObject.Info[];
  benefits: ToolboxObject.Info[];
  category: ToolboxObject.WorkshopCategory | null;
  teaser: string | null;
  checklist: ToolboxObject.ChecklistTask[];
  language: ToolboxObject.Locale;
}) => {
  return createWorkshop({...data, isRecipe: true});
};

export const exportAsRecipe = async (workshopId: number, data: {category: ToolboxObject.WorkshopCategory | null; teaser: string | null}) => {
  return request({
    url: `/workshops/${workshopId}/create-recipe`,
    method: 'POST',
    data: {categoryId: data.category?.id ?? null, teaser: data.teaser},
  });
};

export const duplicateWorkshop = async (workshopId: number, data: {title: string}) => {
  return request({
    url: `/workshops/${workshopId}/duplicate`,
    method: 'POST',
    data,
  });
};

export const deleteWorkshop = async (workshopId: number, data: {isRecipe?: boolean; newOwnerId?: number}) => {
  const {newOwnerId, isRecipe} = data;
  return request({
    url: `/${isRecipe ? 'recipes' : 'workshops'}/${workshopId}`,
    method: 'DELETE',
    data: {
      newOwnerId,
    },
  });
};
export const deleteRecipe = async (workshopId: number) => {
  return deleteWorkshop(workshopId, {isRecipe: true});
};

export const getMethodSubcategories = async () => {
  return request<ToolboxObject.Subcategory[]>({
    url: `/categories/subcategories`,
    method: 'GET',
  });
};

export const getRecipeCategories = async () => {
  return request<ToolboxObject.WorkshopCategory[]>({
    url: `/recipes/categories`,
    method: 'GET',
  });
};
